import { defineStore, storeToRefs } from 'pinia'
import * as SuperTokens from 'supertokens-web-js'
import Session from 'supertokens-web-js/recipe/session'
import EmailPassword from 'supertokens-web-js/recipe/emailpassword'
import EmailVerification from 'supertokens-web-js/recipe/emailverification'
import ThirdParty, {
  getAuthorisationURLWithQueryParamsAndSetState,
  signInAndUp,
} from 'supertokens-web-js/recipe/thirdparty'
import { UserAdmin } from '../../__generated__/graphql'
import { computed, ref } from 'vue'
import { GET_USER_ADMIN } from '@/queries'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { useUserSyncStore } from '@/stores/user-sync/index.ts'
import { usePreferencesStore } from '@/stores/preferences.ts'
import axios from 'axios'
import { useInfoStore } from '@/stores/info.ts'
import { Browser } from '@capacitor/browser'
import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { sendError } from '@/utils/sendError.ts'

export type AuthState = {
  sessionExists: boolean
  userAdmin: UserAdmin | null
}

// --- Persistent auth storage for mobile ---
// On some Android devices, cookies and sessionStorage are not reliably
// persisted across app restarts. We mirror them to Capacitor Preferences
// (backed by Android SharedPreferences) as a fallback.

const isMobile = Capacitor.isNativePlatform()
const SS_PREFIX = 'st-ss-'
// Last-known UserAdmin, so premium/tester (and the offline gate) survive an
// offline restart — GET_USER_ADMIN can't reach the server then.
const USER_ADMIN_CACHE_KEY = 'cachedUserAdmin'
const sessionStorageCache = new Map<string, string>()

export async function restoreAuthState(): Promise<void> {
  if (!isMobile) return

  try {
    const { keys } = await Preferences.keys()

    for (const key of keys) {
      if (key.startsWith(SS_PREFIX)) {
        const ssKey = key.slice(SS_PREFIX.length)
        const { value } = await Preferences.get({ key })
        if (value) {
          window.sessionStorage.setItem(ssKey, value)
          sessionStorageCache.set(ssKey, value)
        }
      }
    }
  } catch (e) {
    sendError(e, { source: 'restoreAuthState' })
  }
}

export function initSuperTokens(): void {
  SuperTokens.init({
    appInfo: {
      appName: 'atsume',
      apiDomain: import.meta.env.VITE_AUTH_API_DOMAIN,
      apiBasePath: import.meta.env.VITE_AUTH_API_BASE_PATH,
    },
    windowHandler: isMobile
      ? (original) => ({
          ...original,
          sessionStorage: {
            key: (index: number) => Promise.resolve(window.sessionStorage.key(index)),
            keySync: (index: number) => window.sessionStorage.key(index),
            getItem: async (key: string) => {
              return window.sessionStorage.getItem(key) ?? sessionStorageCache.get(key) ?? null
            },
            getItemSync: (key: string) => {
              return window.sessionStorage.getItem(key) ?? sessionStorageCache.get(key) ?? null
            },
            setItem: async (key: string, value: string) => {
              window.sessionStorage.setItem(key, value)
              sessionStorageCache.set(key, value)
              await Preferences.set({ key: SS_PREFIX + key, value })
            },
            setItemSync: (key: string, value: string) => {
              window.sessionStorage.setItem(key, value)
              sessionStorageCache.set(key, value)
              Preferences.set({ key: SS_PREFIX + key, value })
            },
            removeItem: async (key: string) => {
              window.sessionStorage.removeItem(key)
              sessionStorageCache.delete(key)
              await Preferences.remove({ key: SS_PREFIX + key })
            },
            removeItemSync: (key: string) => {
              window.sessionStorage.removeItem(key)
              sessionStorageCache.delete(key)
              Preferences.remove({ key: SS_PREFIX + key })
            },
            clear: async () => {
              window.sessionStorage.clear()
              for (const key of sessionStorageCache.keys()) {
                await Preferences.remove({ key: SS_PREFIX + key })
              }
              sessionStorageCache.clear()
            },
            clearSync: () => {
              window.sessionStorage.clear()
              for (const key of sessionStorageCache.keys()) {
                Preferences.remove({ key: SS_PREFIX + key })
              }
              sessionStorageCache.clear()
            },
          },
        })
      : undefined,
    recipeList: [EmailVerification.init(), Session.init({ tokenTransferMethod: 'header' }), EmailPassword.init(), ThirdParty.init()],
  })
}

export const useAuthStore = defineStore('auth', () => {
  const authState = ref<AuthState>({
    sessionExists: false,
    userAdmin: null,
  })
  const { isWeb } = storeToRefs(useInfoStore())
  const httpApi = axios.create({ baseURL: `${import.meta.env.VITE_AUTH_API_DOMAIN}/api` })
  const authErrorHandling = (e: unknown) => {
    console.error(e)
    if (e instanceof Error) {
      return new Error(`unknown error ${e?.message}`)
    }
    return new Error()
  }

  const fetchUser = async (): Promise<UserAdmin> => {
    // Offline: the server is unreachable, so use the cached UserAdmin (keeps
    // premium/tester + the offline gate working after an offline restart).
    if (useInfoStore().isOffline) {
      const { value } = await Preferences.get({ key: USER_ADMIN_CACHE_KEY })
      if (value) {
        const cached = JSON.parse(value) as UserAdmin
        await setUserAdmin(cached)
        return cached
      }
      return Promise.reject(new Error('Offline and no cached user'))
    }
    try {
      const data = await apolloClient.query({
        query: GET_USER_ADMIN,
        fetchPolicy: 'network-only',
      })
      if (!data?.data || !data.data?.userAdmins || data.data.userAdmins.length === 0) {
        if (data?.error) {
          return Promise.reject(
            new Error(
              `Error fetching user: ${JSON.stringify(data.error)}. Count: ${data?.data?.userAdmins?.length}`,
            ),
          )
        }
        return Promise.reject(new Error(`User not found. Count: ${data?.data?.userAdmins?.length}`))
      }
      await setUserAdmin(data.data.userAdmins[0])
      return Promise.resolve(data.data.userAdmins[0])
    } catch (err) {
      console.error(err)
      return Promise.reject(new Error('Error while fetching user'))
    }
  }

  async function setUserAdmin(userAdmin: UserAdmin) {
    authState.value = {
      userAdmin,
      sessionExists: true,
    }
    void Preferences.set({ key: USER_ADMIN_CACHE_KEY, value: JSON.stringify(userAdmin) })
  }

  const deleteUser = async () => {
    try {
      await httpApi.post('deleteUser')
    } catch (e) {
      return authErrorHandling(e)
    }

    await signOut()
  }

  const init = async (): Promise<boolean> => {
    try {
      // Offline (incl. force-offline): refreshing hits /session/refresh and throws,
      // which would look like "no session" and log the user out. Trust the local
      // session instead. (main initializes the info store before auth, so this is
      // reliable here.)
      const hasSession = useInfoStore().isOffline
        ? await Session.doesSessionExist()
        : await Session.attemptRefreshingSession()
      if (hasSession) {
        await fetchUser()
      }
      return hasSession
    } catch (e) {
      sendError(e, { source: 'auth.init' })
      return false
    }
  }

  // Logging in (or out) must never happen under force-offline: the session
  // refresh / auth request would be treated as offline and fail. Clear it first.
  const clearForceOffline = () => usePreferencesStore().setForceOffline(false)

  function dropAnonymousQueryCache(): Promise<void> {
    return apolloClient.cache.reset()
  }

  const signIn = async (email: string, password: string) => {
    try {
      await clearForceOffline()
      const response = await EmailPassword.signIn({
        formFields: [
          { id: 'email', value: email },
          { id: 'password', value: password },
        ],
      })
      if (response.status === 'OK') await dropAnonymousQueryCache()
      return response
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  async function signOut() {
    await clearForceOffline()
    // Close the offline replica cleanly before the post-logout webview reload:
    // an interrupted catalog write would strand the SQLite write lock and break
    // the next session's replica. Dynamic import avoids a static import cycle
    // (catalog-sync depends on this auth store).
    try {
      const { useCatalogSyncStore } = await import('@/stores/catalog-sync')
      await useCatalogSyncStore().prepareForLogout()
    } catch (e) {
      sendError(e, { source: 'auth.signOut.prepareForLogout' })
    }
    try {
      await Session.signOut()
      authState.value = {
        sessionExists: false,
        userAdmin: null,
      }
      await Preferences.remove({ key: USER_ADMIN_CACHE_KEY })
      useUserSyncStore().reset()
      await apolloClient.cache.reset()
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  async function signUp(email: string, password: string, name: string) {
    try {
      await clearForceOffline()
      return await EmailPassword.signUp({
        formFields: [
          { id: 'email', value: email },
          { id: 'password', value: password },
          { id: 'name', value: name },
        ],
      })
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  const sendEmailVerification = async () => {
    try {
      return await EmailVerification.sendVerificationEmail()
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  const verifyEmail = async () => {
    try {
      return await EmailVerification.verifyEmail()
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  async function googleSignInClicked() {
    try {
      await clearForceOffline()
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: 'google',
        frontendRedirectURI: isWeb.value
          ? import.meta.env.VITE_GOOGLE_SSO_REDIRECT_URI
          : import.meta.env.VITE_GOOGLE_SSO_REDIRECT_URI_MOBILE,
      })

      if (isWeb.value) {
        window.location.assign(authUrl)
      } else {
        await Browser.open({ url: authUrl })
      }
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'isSuperTokensGeneralError' in err &&
        'message' in err &&
        err.isSuperTokensGeneralError === true
      ) {
        throw new Error('Custom error from SuperTokens: ' + err.message)
      } else {
        throw err
      }
    }
  }

  async function appleSignInClicked() {
    try {
      await clearForceOffline()
      const authUrl = await getAuthorisationURLWithQueryParamsAndSetState({
        thirdPartyId: 'apple',
        frontendRedirectURI: isWeb.value
          ? import.meta.env.VITE_APPLE_SSO_REDIRECT_URI
          : import.meta.env.VITE_APPLE_SSO_REDIRECT_URI_MOBILE,
        redirectURIOnProviderDashboard: `${import.meta.env.VITE_AUTH_API_DOMAIN}${import.meta.env.VITE_AUTH_API_BASE_PATH}/callback/apple`,
      })

      if (isWeb.value) {
        window.location.assign(authUrl)
      } else {
        await Browser.open({ url: authUrl })
      }
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'isSuperTokensGeneralError' in err &&
        'message' in err &&
        err.isSuperTokensGeneralError === true
      ) {
        throw new Error('Custom error from SuperTokens: ' + err.message)
      } else {
        throw err
      }
    }
  }

  async function handleGoogleCallback(): Promise<'signIn' | 'signUp'> {
    try {
      const response = await signInAndUp()
      if (response.status === 'OK') {
        await dropAnonymousQueryCache()
        if (response.createdNewRecipeUser && response.user.loginMethods.length === 1) {
          return 'signUp'
        } else {
          return 'signIn'
        }
      } else if (response.status === 'SIGN_IN_UP_NOT_ALLOWED') {
        throw new Error('Authentication failed for some reason!')
      } else {
        throw new Error('No email provided by social login. Please use another form of login')
      }
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'isSuperTokensGeneralError' in err &&
        'message' in err &&
        err.isSuperTokensGeneralError === true
      ) {
        throw new Error('Custom error from SuperTokens: ' + err.message)
      } else {
        throw err
      }
    }
  }

  async function handleAppleCallback(): Promise<'signIn' | 'signUp'> {
    try {
      const response = await signInAndUp()
      if (response.status === 'OK') {
        await dropAnonymousQueryCache()
        if (response.createdNewRecipeUser && response.user.loginMethods.length === 1) {
          return 'signUp'
        } else {
          return 'signIn'
        }
      } else if (response.status === 'SIGN_IN_UP_NOT_ALLOWED') {
        throw new Error('Authentication failed for some reason!')
      } else {
        throw new Error('No email provided by social login. Please use another form of login')
      }
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'isSuperTokensGeneralError' in err &&
        'message' in err &&
        err.isSuperTokensGeneralError === true
      ) {
        throw new Error('Custom error from SuperTokens: ' + err.message)
      } else {
        throw err
      }
    }
  }

  const sendPasswordResetEmail = async (email: string) => {
    try {
      return await EmailPassword.sendPasswordResetEmail({
        formFields: [{ id: 'email', value: email }],
      })
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  const submitNewPassword = async (newPassword: string) => {
    try {
      return await EmailPassword.submitNewPassword({
        formFields: [{ id: 'password', value: newPassword }],
      })
    } catch (e) {
      return authErrorHandling(e)
    }
  }

  const hasPremium = computed(() => !!authState.value.userAdmin?.premiumTier)
  const isTester = computed(() => !!authState.value.userAdmin?.isTester)

  return {
    authState,
    hasPremium,
    isTester,
    init,
    signIn,
    signOut,
    signUp,
    deleteUser,
    sendEmailVerification,
    verifyEmail,
    fetchUser,
    googleSignInClicked,
    appleSignInClicked,
    handleGoogleCallback,
    handleAppleCallback,
    sendPasswordResetEmail,
    submitNewPassword,
  }
})
